import { useRef } from 'react';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
    ALLOWED_INVOICE_LABEL,
    INVOICE_ACCEPT_ATTRIBUTE,
    invoiceFileRejectionReason,
    MAX_INVOICE_MB
} from '../../../services/purchaseOrderFiles';

interface VendorInvoiceFileFieldProps {
    /** The newly picked file, not uploaded until the form is submitted. */
    file: File | null;
    /** A document already attached to the PO, so the label reads "Replace". */
    hasStoredFile: boolean;
    onPick: (file: File | null) => void;
    /** Why a pick was refused - the caller surfaces it. */
    onReject: (reason: string) => void;
}

export function VendorInvoiceFileField({
    file,
    hasStoredFile,
    onPick,
    onReject
}: VendorInvoiceFileFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null);

    const takeFile = (picked: File | undefined) => {
        // Cleared so re-picking the same file after a removal still fires onChange.
        if (inputRef.current) inputRef.current.value = '';
        if (!picked) return;
        const rejection = invoiceFileRejectionReason(picked);
        if (rejection) {
            onReject(rejection);
            return;
        }
        onPick(picked);
    };

    return (
        <div>
            <span className="block text-xs font-semibold text-gray-700 mb-1">Invoice document</span>
            <input
                ref={inputRef}
                type="file"
                accept={INVOICE_ACCEPT_ATTRIBUTE}
                onChange={(e) => takeFile(e.target.files?.[0])}
                className="hidden"
            />
            {file ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white">
                    <PaperClipIcon className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-700 truncate" title={file.name}>
                        {file.name}
                    </span>
                    <button
                        type="button"
                        onClick={() => onPick(null)}
                        aria-label="Remove document"
                        className="ml-auto text-gray-400 hover:text-gray-700 transition-colors"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-md border border-dashed border-gray-300 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
                >
                    <PaperClipIcon className="h-4 w-4 text-gray-400" />
                    {hasStoredFile ? 'Replace attached document' : 'Attach a document'}
                </button>
            )}
            <p className="text-[11px] text-gray-400 mt-1">
                {ALLOWED_INVOICE_LABEL}, up to {MAX_INVOICE_MB}MB.
            </p>
        </div>
    );
}
